/**
 * Invitations API endpoints
 *
 * All invitation-related API calls go through this module.
 *
 * Constitution Section 19.4: API Response Validation (Zod Mandatory)
 */

import { apiClient, isAxiosError } from '../client';
import {
  UserInvitationsResponseSchema,
  RejectInvitationResponseSchema,
  AcceptInvitationResponseSchema,
  type UserInvitationsResponse,
  type RejectInvitationResponse,
  type AcceptInvitationResponse,
} from '@/schemas/invitation';

// Re-export types for convenience
export type { UserInvitationsResponse, RejectInvitationResponse, AcceptInvitationResponse };

/** Safe fallback when /invitations/me returns 400/401 (e.g. email not in token before backend resolution) */
const EMPTY_INVITATIONS_RESPONSE: UserInvitationsResponse = {
  invitations: [],
  pending_count: 0,
};

/**
 * Get user's invitations with optional status filter
 *
 * On 400/401 (e.g. auth/email resolution issues), returns empty list so UI does not break.
 *
 * @param status - 'pending' (default) or 'all'
 * @returns Invitations list with pending count
 */
export async function getMyInvitations(
  status: 'pending' | 'all' = 'pending'
): Promise<UserInvitationsResponse> {
  try {
    const response = await apiClient.get<unknown>('/invitations/me', {
      params: { status },
    });

    // Zod parse at API boundary (Section 19.4)
    const data = UserInvitationsResponseSchema.parse(response.data);

    return data;
  } catch (err) {
    if (isAxiosError(err) && err.response && [400, 401].includes(err.response.status)) {
      return EMPTY_INVITATIONS_RESPONSE;
    }
    throw err;
  }
}

/**
 * Reject an invitation
 *
 * Idempotent: returns success even if already rejected
 *
 * @param invitationId - The invitation ID to reject
 * @returns Success response
 */
export async function rejectInvitation(
  invitationId: number
): Promise<RejectInvitationResponse> {
  const response = await apiClient.post<unknown>(
    `/invitations/${invitationId}/reject`
  );

  // Zod parse at API boundary (Section 19.4)
  const data = RejectInvitationResponseSchema.parse(response.data);

  return data;
}

/**
 * Accept an invitation by ID
 *
 * @param invitationId - The invitation ID to accept
 * @returns Success response with company/project info
 */
export async function acceptInvitationById(
  invitationId: number
): Promise<AcceptInvitationResponse> {
  const response = await apiClient.post<unknown>(
    `/invitations/${invitationId}/accept`
  );

  // Zod parse at API boundary (Section 19.4)
  const data = AcceptInvitationResponseSchema.parse(response.data);

  return data;
}
